export const SCENE_TOOLBAR_GAP_PX = 6
export const SCENE_TOOLBAR_MORE_RESERVE_PX = 36

/** Drop secondary chrome first; keep save / add actions on the row when present. */
export const SCENE_OVERFLOW_DROP_ORDER = [
  "help",
  "qr",
  "viewQr",
  "speed",
  "fov",
  "platform",
  "light",
  "markup",
  "trajectory",
  "history",
  "redo",
  "undo",
] as const

export type SceneOverflowKey = (typeof SCENE_OVERFLOW_DROP_ORDER)[number]

export type SceneToolbarMeasuredWidths = {
  saveStart: number
  goToStart: number
  add: number
  markup: number
  undo: number
  redo: number
  history: number
  trajectory: number
  light: number
  fov: number
  platform: number
  speed: number
  help: number
  qr: number
  viewQr: number
  divider: number
}

export const FALLBACK_SCENE_TOOLBAR_WIDTHS: SceneToolbarMeasuredWidths = {
  saveStart: 32,
  goToStart: 32,
  add: 32,
  markup: 32,
  undo: 32,
  redo: 32,
  history: 32,
  trajectory: 32,
  light: 32,
  fov: 64,
  platform: 32,
  speed: 32,
  help: 32,
  qr: 32,
  viewQr: 32,
  divider: 9,
}

export type SceneToolbarPresent = {
  saveStart: boolean
  goToStart: boolean
  canEdit: boolean
  qr: boolean
  viewQr: boolean
  trajectory: boolean
  /** Desktop only — hotkey help; false on phone chrome. */
  help: boolean
  /** Phone fly entry. Stays on the row, next to add. */
  phoneFly?: boolean
}

export function sceneOverflowSetsEqual(a: Set<SceneOverflowKey>, b: Set<SceneOverflowKey>): boolean {
  if (a.size !== b.size) return false
  for (const key of a) {
    if (!b.has(key)) return false
  }
  return true
}

function widthOf(key: string, widths: SceneToolbarMeasuredWidths): number {
  if (key === "div") return widths.divider
  return widths[key as keyof SceneToolbarMeasuredWidths] ?? 32
}

function visibleKeys(hidden: Set<string>, present: SceneToolbarPresent): string[] {
  const keys: string[] = []
  if (present.canEdit) {
    if (present.saveStart) keys.push("saveStart")
    if (present.goToStart) keys.push("goToStart")
    if (!hidden.has("undo")) keys.push("undo")
    if (!hidden.has("redo")) keys.push("redo")
    if (!hidden.has("history")) keys.push("history")
  }
  const afterHistory =
    (present.canEdit &&
      ((present.trajectory && !hidden.has("trajectory")) ||
        !hidden.has("markup") ||
        !hidden.has("light") ||
        !hidden.has("platform") ||
        !hidden.has("fov"))) ||
    (present.qr && !hidden.has("qr")) ||
    (present.viewQr && !hidden.has("viewQr"))
  if (afterHistory && keys.length > 0) keys.push("div")
  if (present.canEdit) {
    keys.push("add")
    if (present.phoneFly) keys.push("phoneFly")
    if (present.trajectory && !hidden.has("trajectory")) keys.push("trajectory")
    if (!hidden.has("markup")) keys.push("markup")
    if (!hidden.has("light")) keys.push("light")
    if (!hidden.has("fov")) keys.push("fov")
    if (!hidden.has("platform")) keys.push("platform")
  }
  if (present.qr && !hidden.has("qr")) keys.push("qr")
  if (present.viewQr && !hidden.has("viewQr")) keys.push("viewQr")
  if (present.help && !hidden.has("help")) keys.push("help")
  return keys
}

export function sceneToolbarRowWidth(
  hidden: Set<string>,
  present: SceneToolbarPresent,
  widths: SceneToolbarMeasuredWidths = FALLBACK_SCENE_TOOLBAR_WIDTHS,
  showMore = false,
): number {
  const keys = visibleKeys(hidden, present)
  if (keys.length === 0) return showMore ? SCENE_TOOLBAR_MORE_RESERVE_PX : 0
  let width = 0
  for (let i = 0; i < keys.length; i++) {
    if (i > 0) width += SCENE_TOOLBAR_GAP_PX
    width += widthOf(keys[i], widths)
  }
  if (showMore) width += SCENE_TOOLBAR_GAP_PX + SCENE_TOOLBAR_MORE_RESERVE_PX
  return width
}

export function computeSceneToolbarFit(
  available: number,
  present: SceneToolbarPresent,
  widths: SceneToolbarMeasuredWidths = FALLBACK_SCENE_TOOLBAR_WIDTHS,
): Set<SceneOverflowKey> {
  const hidden = new Set<SceneOverflowKey>()
  const fits = () => sceneToolbarRowWidth(hidden, present, widths, hidden.size > 0) <= available + 0.5

  if (fits()) return hidden

  for (const key of SCENE_OVERFLOW_DROP_ORDER) {
    if (
      !present.canEdit &&
      (key === "platform" ||
        key === "fov" ||
        key === "light" ||
        key === "markup" ||
        key === "history" ||
        key === "redo" ||
        key === "undo")
    ) {
      continue
    }
    if (key === "qr" && !present.qr) continue
    if (key === "viewQr" && !present.viewQr) continue
    if (key === "help" && !present.help) continue
    if (key === "trajectory" && !present.trajectory) continue
    hidden.add(key)
    if (fits()) return hidden
  }
  return hidden
}
